/*
 * 快速加购弹窗 —— 在当前页面选颜色/尺寸并加购，不跳转。
 * 配套 snippets/quick-buy-modal.liquid，原生 JS 无依赖，幂等（可重复载入）。
 *
 * 只负责三件事：开关弹窗、选项联动、调用既有加购函数。
 *
 * ⚠ 不做任何金额运算。每个变体的最终价格字符串（含优惠券折扣）已经由
 *   quick-buy-modal.liquid 用 Liquid 滤镜算好写进 [data-qb-variants]，
 *   这里只换字符串。放 JS 里算 = 折扣显示逻辑出现第二个真相源，
 *   而店里的优惠券是 metaobject 按集合驱动的（见 snippets/price.liquid），迟早对不齐。
 *
 * ⚠ 加购走 utils.js 的 addMultipleToCart()，不要自己写 fetch。
 *   那个函数里处理了三个已经踩过的坑：422 被当成成功、cartData 没刷新就解禁按钮、
 *   cart-drawer.js 的定时同步抢序号导致 await 提前 resolve。
 */
(function () {
  'use strict';

  if (window.__quickBuyInit) return;
  window.__quickBuyInit = true;

  var OPEN_CLASS = 'qb-modal--open';
  var activeModal = null;
  var lastOpener = null;
  var prevOverflow = null;

  /* ---------- 埋点 ---------- */

  /*
   * 走 docs/tracking-plan.md 的登记口径：全站 dataLayer 类统一 event:'track_event'
   * 单触发器，事件名放 tracking.event_name。参数只用已注册维度，不造新词。
   * quick_buy_open / quick_buy_add 已于 2026-09-02 登记进 tracking-plan.md。
   */
  /*
   * ⚠ 整个函数体必须包在 try/catch 里，绝不能把异常抛给调用方。
   *
   * dataLayer.push 不是我们的代码：GTM 载入后会把它换成自己的实现，第三方脚本
   * 也可能再包一层。它一旦抛错，异常会顺着调用栈落进 addToCart 的 catch，
   * 被当成"加购失败"——而商品其实**已经进车了**。顾客看到报错、按钮重新可用，
   * 再点一次就是重复加购。埋点坏了最多是少一条数据，不能变成多收一件货。
   */
  function track(eventName, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'track_event',
        tracking: {
          event_name: eventName,
          interaction: eventName === 'quick_buy_add' ? 'click' : 'view',
          measurement_version: 1,
          params: params || {}
        }
      });
    } catch (e) {
      console.error('[quick-buy] 埋点上报失败（已忽略，不影响加购）', e);
    }
  }

  /* ---------- 状态读写 ---------- */

  // Liquid 按当前 storefront locale 输出；保留英语后备，兼容缓存中的旧弹窗。
  function copy(modal, key, fallback) {
    if (!modal.__qbCopy) {
      var node = modal.querySelector('[data-qb-copy]');
      try {
        modal.__qbCopy = node ? JSON.parse(node.textContent) : {};
      } catch (e) {
        modal.__qbCopy = {};
      }
    }
    return modal.__qbCopy[key] || fallback;
  }

  function readVariants(modal) {
    if (modal.__qbVariants) return modal.__qbVariants;
    var node = modal.querySelector('[data-qb-variants]');
    var list = [];
    try {
      list = JSON.parse(node.textContent);
    } catch (e) {
      // 解析失败就让弹窗退化成"只能加默认变体"，而不是整个坏掉
      console.error('[quick-buy] 变体数据解析失败', e);
    }
    modal.__qbVariants = list;
    return list;
  }

  /*
   * selected 必须覆盖**全部**选项位，包括没渲染出来的那些。
   * 只有一个取值的选项（比如唯一颜色叫 "Bird"）不渲染，但它仍然占着 v.options 的一个位置，
   * 不补上的话 every() 永远匹配不到变体。
   */
  function readSelection(modal) {
    var addBtn = modal.querySelector('[data-qb-add]');
    var defaultId = parseInt(addBtn.getAttribute('data-variant-id'), 10);
    var variants = readVariants(modal);
    var base = null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].id === defaultId) {
        base = variants[i].opts.slice();
        break;
      }
    }
    if (!base) base = variants.length ? variants[0].opts.slice() : [];

    modal.querySelectorAll('[data-qb-option]').forEach(function (group) {
      var idx = parseInt(group.getAttribute('data-option-index'), 10);
      var sel = group.querySelector('.qb-swatch.is-selected');
      if (sel) base[idx] = sel.getAttribute('data-qb-value');
    });
    return base;
  }

  function findVariant(variants, selection) {
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var ok = true;
      for (var j = 0; j < selection.length; j++) {
        if (v.opts[j] !== selection[j]) {
          ok = false;
          break;
        }
      }
      if (ok) return v;
    }
    return null;
  }

  /* ---------- 渲染 ---------- */

  function render(modal) {
    var variants = readVariants(modal);
    var selection = readSelection(modal);
    var variant = findVariant(variants, selection);

    /*
     * 选出来的组合不存在（颜色 A 没有 尺寸 X）—— 不要留在死状态。
     * 退到"保持刚改的那一项，其余按第一个可售变体重排"。
     */
    if (!variant) {
      var fallback = null;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].available) {
          fallback = variants[i];
          break;
        }
      }
      variant = fallback || variants[0];
      if (variant) {
        modal.querySelectorAll('[data-qb-option]').forEach(function (group) {
          var idx = parseInt(group.getAttribute('data-option-index'), 10);
          selectValue(group, variant.opts[idx]);
        });
        selection = variant.opts.slice();
      }
    }
    if (!variant) return;

    /*
     * 价格：只换字符串 + 切一个类。
     *
     * ⚠ 必须用 innerHTML，不能用 textContent。
     * 店里装了货币转换 app，它改的是 shop.money_format 本身 —— Liquid 的 money 滤镜
     * 吐出来的不是 "$39.00" 而是 "<span class=glc-money>$39.00</span>"。
     * 用 textContent 的话顾客会看到一串标签源码。
     * 这段 HTML 是 Liquid 服务端生成的（见 quick-buy-modal.liquid 里预算好的 now/was），
     * 不含任何用户输入，innerHTML 安全。
     */
    var priceBox = modal.querySelector('[data-qb-price]');
    var reg = modal.querySelector('[data-qb-price-regular]');
    var sale = modal.querySelector('[data-qb-price-sale]');
    var cmp = modal.querySelector('[data-qb-price-compare]');
    if (reg) reg.innerHTML = variant.now;
    if (sale) sale.innerHTML = variant.now;
    if (cmp) cmp.innerHTML = variant.was || '';
    if (priceBox) priceBox.classList.toggle('qb-price--on-sale', !!variant.onSale);

    // 折扣标签同理：金额随变体变，所以每个变体的文字都是 Liquid 预算好的
    // （snippets/discount-label.liquid），这里只换字符串，规则不在前端重写一份。
    var tag = modal.querySelector('[data-qb-price-tag]');
    if (tag) {
      tag.innerHTML = variant.tag || '';
      if (tag.parentNode) tag.parentNode.style.display = variant.tag ? '' : 'none';
    }

    /*
     * 主图。variant.img 为空时必须**显式回退**到产品主图，不能保持原图 ——
     * 否则先选一个有图的颜色、再选没图的颜色，弹窗会继续展示上一个颜色，
     * 等于告诉顾客这个色长那样。美国在售商品里现在就有三个能触发：
     * Arden Throw Pillowcase 的 Beige、Hanging Ball Tieback 的 Grey、
     * Zane Throw Pillowcase 的 Ivory（2026-09-02 盘点，见 swatch_gaps）。
     */
    var img = modal.querySelector('[data-qb-image]');
    if (img) {
      var src = variant.img || modal.getAttribute('data-fallback-img') || '';
      if (src && img.getAttribute('src') !== src) img.src = src;
    }
    // 带图库的弹窗（qb_gallery）：滑到这个变体自己的那张图；没有变体图就停在当前位置
    if (variant.mid) gallerySync(modal, variant.mid);

    // 每个选项组的当前值文字
    modal.querySelectorAll('[data-qb-option]').forEach(function (group) {
      var idx = parseInt(group.getAttribute('data-option-index'), 10);
      var label = group.querySelector('[data-qb-option-value]');
      if (label) label.textContent = selection[idx] || '';
    });

    /*
     * 可选性分**两档**，不能只按"保持其它选项不变时有没有货"一刀切。
     *
     * 只按上下文算会造出走不到的库存孤岛：假设只有 Beige/Small 和 Black/Large 有货，
     * 默认停在 Beige/Small 时，Black 因 Black/Small 无货被禁、Large 因 Beige/Large
     * 无货也被禁 —— Black/Large 明明有货，却没有任何点击路径能到达。
     *
     *   anywhere  = 全店任何组合下这个取值还有货吗 → 没有才是真·售罄，禁用
     *   inContext = 保持其它选项不变时有货吗 → 没有只是"这个搭配没货"，
     *               仍然可点，点了自动把其它选项调到含它的第一个可售变体
     */
    modal.querySelectorAll('[data-qb-option]').forEach(function (group) {
      var idx = parseInt(group.getAttribute('data-option-index'), 10);
      group.querySelectorAll('.qb-swatch').forEach(function (btn) {
        var value = btn.getAttribute('data-qb-value');
        var anywhere = variants.some(function (v) {
          return v.opts[idx] === value && v.available;
        });
        var inContext = variants.some(function (v) {
          if (v.opts[idx] !== value) return false;
          for (var j = 0; j < selection.length; j++) {
            if (j !== idx && v.opts[j] !== selection[j]) return false;
          }
          return v.available;
        });

        btn.classList.toggle('is-unavailable', !anywhere);
        btn.classList.toggle('is-unmatched', anywhere && !inContext);
        btn.setAttribute('aria-pressed', btn.classList.contains('is-selected') ? 'true' : 'false');
        /*
         * 真·售罄要让读屏拿得到：aria-disabled 而不是 disabled ——
         * disabled 会让按钮跳出 Tab 序列，用户根本发现不了这个颜色存在；
         * aria-disabled 保留可聚焦，配合隐藏文案能读出"已售罄"。
         * "这个搭配没货"不算禁用，它是可点的。
         */
        btn.setAttribute('aria-disabled', anywhere ? 'false' : 'true');
        var soldOut = btn.querySelector('[data-qb-soldout]');
        if (soldOut) soldOut.hidden = anywhere;
      });
    });

    // 加购按钮
    var addBtn = modal.querySelector('[data-qb-add]');
    var addLabel = modal.querySelector('[data-qb-add-label]');
    addBtn.setAttribute('data-variant-id', variant.id);
    // 按钮里的价格：和上面一样只换 Liquid 预算好的字符串（innerHTML，原因同上）；售罄时不显示价格
    var addPrice = modal.querySelector('[data-qb-add-price]');
    if (addPrice) {
      addPrice.innerHTML = variant.now;
      if (addPrice.parentNode) addPrice.parentNode.hidden = !variant.available;
    }
    renderQuantity(modal);
    // busy 也要算进去：否则 render() 会在加购途中把按钮重新启用，
    // 看着能点、点了又被 addToCart 的 busy 守卫挡掉，等于一颗没反应的按钮
    addBtn.disabled = modal.__qbBusy || !variant.available;
    // 文案取 theme.liquid:662-665 注入的 window.variantStrings（不是 window.theme，
    // 那个对象上只有 moneyFormat / quickviewMore / reviews）
    if (addLabel && !modal.__qbBusy) {
      var strings = window.variantStrings || {};
      addLabel.textContent = variant.available
        ? strings.addToCart || 'ADD TO CART'
        : strings.soldOut || 'SOLD OUT';
    }
  }

  /*
   * 数量（主题 <quantity-input>，见 quick-buy-modal.liquid）。
   * 读不到合法正整数就按 1 —— 主题步进器自己也是把非法输入改回 1。
   */
  /* ---------- 图库（只在 quick-buy-modal 传了 qb_gallery 时存在） ---------- */

  /*
   * 原生横向滚动吸附轮播（AGENTS.md「Media carousels and native gestures」）：
   * 图片/视频真实放在 overflow-x 的轨道里，触控板双指横滑、手机手指滑、鼠标按箭头/缩略图都走同一条滚动；
   * 当前张、计数、缩略图都从真实 scrollLeft 算，不拿 wheel 事件模拟。
   * 点图放大：桌面跟着鼠标位置看细节，手机在放大的图里拖动查看；放大时锁住轨道横滑，再点一下还原。
   * 切走 / 关弹窗时暂停非当前视频、还原放大。
   */
  function galleryParts(modal) {
    var root = modal.querySelector('[data-qb-gallery]');
    if (!root) return null;
    return {
      root: root,
      track: root.querySelector('[data-qb-gallery-track]'),
      slides: Array.prototype.slice.call(root.querySelectorAll('[data-qb-slide]')),
      thumbs: Array.prototype.slice.call(root.querySelectorAll('[data-qb-thumb]')),
      count: root.querySelector('[data-qb-gallery-count]'),
      prev: root.querySelector('[data-qb-gallery-prev]'),
      next: root.querySelector('[data-qb-gallery-next]')
    };
  }

  function galleryIndex(g) {
    if (!g.track || !g.track.clientWidth) return 0;
    return Math.max(0, Math.min(g.slides.length - 1, Math.round(g.track.scrollLeft / g.track.clientWidth)));
  }

  function galleryGo(g, index, smooth) {
    if (!g.track) return;
    index = Math.max(0, Math.min(g.slides.length - 1, index));
    g.track.scrollTo({ left: index * g.track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
  }

  function galleryUnzoom(g) {
    g.root.classList.remove('is-zoomed');
    g.slides.forEach(function (slide) {
      slide.classList.remove('is-zoomed');
      var img = slide.querySelector('img');
      if (img) img.style.transformOrigin = '';
    });
  }

  function galleryUpdate(g) {
    var index = galleryIndex(g);
    if (g.count) g.count.textContent = (index + 1) + ' / ' + g.slides.length;
    g.thumbs.forEach(function (thumb, i) {
      thumb.classList.toggle('is-active', i === index);
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
    if (g.prev) g.prev.disabled = index === 0;
    if (g.next) g.next.disabled = index === g.slides.length - 1;
    g.slides.forEach(function (slide, i) {
      var video = slide.querySelector('video');
      if (video && i !== index && !video.paused) video.pause();
      if (i !== index && slide.classList.contains('is-zoomed')) galleryUnzoom(g);
    });
  }

  function galleryInit(modal) {
    var g = galleryParts(modal);
    if (!g || !g.track) return;
    if (g.root.__qbGalleryReady) {
      galleryUpdate(g);
      return;
    }
    g.root.__qbGalleryReady = true;

    var raf = null;
    g.track.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        galleryUpdate(g);
      });
    }, { passive: true });

    if (g.prev) g.prev.addEventListener('click', function () { galleryGo(g, galleryIndex(g) - 1, true); });
    if (g.next) g.next.addEventListener('click', function () { galleryGo(g, galleryIndex(g) + 1, true); });
    g.thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { galleryGo(g, i, true); });
    });

    // 键盘：轨道聚焦时 ←/→ 翻张（滚动容器本身也能用方向键滚，这里让它一次正好一张）
    g.track.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        galleryGo(g, galleryIndex(g) + (event.key === 'ArrowRight' ? 1 : -1), true);
      }
    });

    g.slides.forEach(function (slide) {
      var zoomBtn = slide.querySelector('[data-qb-zoom]');
      var img = slide.querySelector('img');
      if (!zoomBtn || !img) return;
      var dragging = false;
      var moved = false;

      function setOrigin(event) {
        var rect = slide.getBoundingClientRect();
        var x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
        var y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
        img.style.transformOrigin = x + '% ' + y + '%';
      }

      zoomBtn.addEventListener('click', function (event) {
        if (moved) {
          moved = false;
          return;
        }
        var zoomed = !slide.classList.contains('is-zoomed');
        galleryUnzoom(g);
        if (zoomed) {
          slide.classList.add('is-zoomed');
          g.root.classList.add('is-zoomed');
          setOrigin(event);
        }
        zoomBtn.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
      });

      // 放大后：鼠标移动 / 手指拖动改变放大中心
      slide.addEventListener('pointermove', function (event) {
        if (!slide.classList.contains('is-zoomed')) return;
        if (event.pointerType === 'mouse' || dragging) {
          if (dragging) moved = true;
          setOrigin(event);
        }
      });
      slide.addEventListener('pointerdown', function (event) {
        if (slide.classList.contains('is-zoomed') && event.pointerType !== 'mouse') {
          dragging = true;
          moved = false;
        }
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (type) {
        slide.addEventListener(type, function () { dragging = false; });
      });
    });

    galleryUpdate(g);
  }

  function gallerySync(modal, mediaId) {
    var g = galleryParts(modal);
    if (!g || !g.track) return;
    for (var i = 0; i < g.slides.length; i++) {
      if (String(g.slides[i].getAttribute('data-media-id')) === String(mediaId)) {
        if (i !== galleryIndex(g)) galleryGo(g, i, false);
        return;
      }
    }
  }

  function galleryReset(modal) {
    var g = galleryParts(modal);
    if (!g) return;
    galleryUnzoom(g);
    g.root.querySelectorAll('video').forEach(function (video) {
      if (!video.paused) video.pause();
    });
  }

  function readQuantity(modal) {
    var input = modal.querySelector('[data-qb-quantity]');
    var n = input ? parseInt(input.value, 10) : 1;
    return n >= 1 ? n : 1;
  }

  /*
   * 数量大于 1 时按钮价格后面跟「× N」。不在这里乘出总价：价格字符串可能被货币转换 app
   * 包成 <span class="glc-money">，前端算出来的数它不会再转换（文件头的「不做金额运算」）。
   */
  function renderQuantity(modal) {
    var times = modal.querySelector('[data-qb-add-times]');
    if (!times) return;
    var n = readQuantity(modal);
    times.textContent = n > 1 ? ' × ' + n : '';
  }

  // 加购进行中锁住数量：提交时数量已经取走，再改只会让按钮上的「× N」和进车的数量对不上
  function setQuantityLocked(modal, locked) {
    modal.querySelectorAll('.qb-modal__qty input, .qb-modal__qty button').forEach(function (control) {
      control.disabled = locked;
    });
  }

  function selectValue(group, value) {
    group.querySelectorAll('.qb-swatch').forEach(function (btn) {
      btn.classList.toggle('is-selected', btn.getAttribute('data-qb-value') === value);
    });
  }

  /* ---------- 开 / 关 ---------- */

  /*
   * 弹窗必须挂到 <body> 下。
   * 1) position:fixed 只要祖先有 transform/filter/will-change 就会被困在那个层叠上下文里 ——
   *    PDP 和购物车抽屉里这类属性到处都是，留在原地随时可能被裁掉或压住。
   * 2) 购物车抽屉的内容会被 renderContents 整块替换，弹窗留在里面会跟着被销毁。
   * 搬之前先清掉 body 下同 id 的旧节点，否则抽屉重渲染后会出现两个同 id，
   * getElementById 拿到的是旧的那个（数据已经过期）。
   */
  function ensureInBody(modal) {
    if (modal.parentElement === document.body) return modal;
    var id = modal.id;
    if (id) {
      var stale = document.body.querySelector(':scope > [id="' + id + '"]');
      if (stale && stale !== modal) stale.remove();
    }
    document.body.appendChild(modal);
    return modal;
  }

  function open(modal, opener) {
    modal = ensureInBody(modal);
    if (activeModal && activeModal !== modal) close(activeModal);

    lastOpener = opener || null;
    activeModal = modal;
    modal.hidden = false;
    modal.classList.add(OPEN_CLASS);

    // 抽屉可能已经锁过滚动，记下原值再改，关闭时原样还回去
    // 锁在 body（同主题 .overflow-hidden）。不能锁 html：html 一旦不是 overflow:visible，
    // body 的 overflow 不再传给视口、body 自己变成滚动容器，PDP 左侧 sticky 画廊退回页顶看不见（TH-QA-566）。
    // 用行内样式而不是 .overflow-hidden 类：那个类是抽屉等面板共享的，抽屉关闭时 releaseThemeScrollLock 会把它摘掉。
    if (prevOverflow === null) {
      prevOverflow = document.body.style.overflow || '';
      document.body.style.overflow = 'hidden';
    }

    // 每次打开从 1 件开始：弹窗节点会被缓存复用，上一次选的数量不该带到这一次
    var qtyInput = modal.querySelector('[data-qb-quantity]');
    if (qtyInput && !modal.__qbBusy) {
      qtyInput.value = 1;
      var stepper = qtyInput.closest('quantity-input');
      if (stepper && typeof stepper.setMinimumDisable === 'function') stepper.setMinimumDisable();
    }

    render(modal);
    galleryInit(modal);

    // 底下的抽屉若开着，先让它对辅助技术消失，再把焦点圈进弹窗
    if (isDrawerActive()) setDrawerInert(true);

    /*
     * 焦点必须圈在弹窗里。只做 card.focus() 是不够的 —— 键盘用户继续按 Tab
     * 就走出弹窗，落到遮罩后面的页面上；从购物车推荐位打开时更明显：
     * 旧的 iframe 路径会先把抽屉关掉，我们这条保留抽屉，
     * 不圈定的话 Tab 会直接跑进底下那个抽屉里。
     * 用主题既有的 global.js:510 trapFocus，不自己写一套。
     */
    var card = modal.querySelector('.qb-modal__card');
    if (card) {
      if (typeof trapFocus === 'function') {
        trapFocus(card, card);
      } else {
        card.focus({ preventScroll: true });
      }
    }

    track('quick_buy_open', {
      popup_name: 'quick_buy',
      fired_from: modal.__qbSource || modal.getAttribute('data-source') || 'unknown'
    });
  }

  function close(modal) {
    if (!modal) return;
    modal.classList.remove(OPEN_CLASS);
    modal.hidden = true;
    if (activeModal === modal) activeModal = null;
    if (!activeModal && prevOverflow !== null) {
      document.body.style.overflow = prevOverflow;
      prevOverflow = null;
    }
    /*
     * 焦点交接。
     *
     * ⚠ 抽屉还开着的时候，不能只 removeTrapFocus 就完事：
     * 我们打开弹窗时调的 trapFocus() 内部会先 removeTrapFocus()，把**抽屉原有的**
     * 圈定一并摘掉了；而 cart-drawer.js:44 是把 trapFocus 挂在 transitionend
     * 且 {once:true} —— 抽屉本来就是 active，再调 open() 不会产生过渡，
     * 那个监听永远不会再触发。不在这里补回去的话，关掉弹窗继续按 Tab
     * 就能走出仍然开着的抽屉，跑到背后的页面上。
     */
    // ⚠ 必须先解除 inert：inert 容器里的元素聚焦不了，
    // 不先恢复的话下面 trapDrawerFocus(back) 会静默失败，焦点掉回 body。
    setDrawerInert(false);

    var back = lastOpener && document.contains(lastOpener) ? lastOpener : null;
    if (!back) back = document.querySelector('#CartDrawer .product-recommendations__list button');

    if (!trapDrawerFocus(back)) {
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(back);
      } else if (back) {
        back.focus({ preventScroll: true });
      }
    }

    lastOpener = null;
    setError(modal, '');
    setStatus(modal, '');
    galleryReset(modal);

    // 刷新时被标记为过期的抽屉弹窗，留到这里才真正删（见 refreshCartDrawer 的注释）
    if (modal.__qbStale) modal.remove();
  }

  /*
   * 把焦点圈定还给仍然开着的购物车抽屉。容器和聚焦目标的选法照抄
   * cart-drawer.js:47-53，保持和它自己 open() 时的行为一致。
   * 返回 true 表示已经接管，调用方不用再 removeTrapFocus。
   */
  function isDrawerActive() {
    var drawer = document.querySelector('cart-drawer');
    return !!drawer && drawer.classList.contains('active');
  }

  /*
   * 记录一次异步弹窗加载期间，购物车抽屉是否**曾经**关闭过。
   *
   * 只在请求回来时检查 classList.contains('active') 不够：用户完全可能
   * 「点 ADD → 关抽屉 → 又打开抽屉」，此时快照重新变成 active，旧请求就会
   * 把已经取消的弹窗再次冒出来。MutationObserver 保留这段生命周期历史。
   *
   * attributeOldValue + 下一条记录的 oldValue 用来还原每一次 class 变化，
   * 所以即使 close/open 在同一个任务里连续发生、回调执行时最终状态已是 active，
   * 中间那次 active → inactive 仍然不会漏掉。
   */
  function watchDrawerClosure(drawer) {
    if (!drawer || typeof MutationObserver !== 'function') return null;

    var closed = false;
    var hasActive = function (value) {
      return /(^|\s)active(?:\s|$)/.test(value || '');
    };
    var inspect = function (records) {
      for (var i = 0; i < records.length; i++) {
        var before = records[i].oldValue || '';
        var after =
          i + 1 < records.length
            ? records[i + 1].oldValue || ''
            : drawer.getAttribute('class') || '';
        if (hasActive(before) && !hasActive(after)) {
          closed = true;
          break;
        }
      }
    };
    var observer = new MutationObserver(inspect);
    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true
    });

    return {
      wasClosed: function () {
        inspect(observer.takeRecords());
        return closed;
      },
      disconnect: function () {
        inspect(observer.takeRecords());
        observer.disconnect();
      }
    };
  }

  /*
   * 弹窗开着的时候，把底下的购物车抽屉对辅助技术屏蔽掉。
   *
   * cart-drawer.liquid:178-182 的 .drawer__inner 自己就是
   * role="dialog" aria-modal="true"；我们的弹窗挂在 body 下，又是一个同级的
   * aria-modal 对话框。两个同时"活着"，读屏判断不出当前对话框是哪一个，
   * 朗读范围也会串。焦点圈定只解决键盘 Tab，解决不了这个。
   *
   * inert 同时管住焦点和辅助技术；老浏览器不认 inert，所以 aria-hidden 一起加。
   * 弹窗本身在 body 下、不在 .drawer__inner 里面，不会被这层 inert 波及。
   */
  function setDrawerInert(on) {
    var inner = document.querySelector('cart-drawer .drawer__inner');
    if (!inner) return;
    if (on) {
      inner.setAttribute('aria-hidden', 'true');
      inner.inert = true;
    } else {
      inner.removeAttribute('aria-hidden');
      inner.inert = false;
    }
  }

  /*
   * preferredFocus：优先把焦点还给刚才那颗触发按钮。
   * 不传（或按钮已经被刷新换掉）时才退回抽屉容器 —— 直接扔到 .drawer__inner
   * 等于把键盘用户送回抽屉顶部，推荐项多的时候要重新 Tab 很长一段才回到原位。
   */
  function trapDrawerFocus(preferredFocus) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawer.classList.contains('active')) return false;
    if (typeof trapFocus !== 'function') return false;

    var container = drawer.classList.contains('is-empty')
      ? drawer.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    if (!container) return false;

    var focusEl =
      preferredFocus && document.contains(preferredFocus) && container.contains(preferredFocus)
        ? preferredFocus
        : drawer.querySelector('.drawer__inner') || drawer.querySelector('.drawer__close') || container;
    trapFocus(container, focusEl); // 内部会先 removeTrapFocus，不会叠监听
    return true;
  }

  function setError(modal, msg) {
    var node = modal.querySelector('[data-qb-error]');
    if (!node) return;
    node.textContent = msg || '';
    node.hidden = !msg;
  }

  // 加购进度的读屏播报。视觉上按钮自己会变文字，这里只服务读屏。
  function setStatus(modal, msg) {
    var node = modal.querySelector('[data-qb-status]');
    if (node) node.textContent = msg || '';
  }

  /* ---------- 超时 ---------- */

  /*
   * 给一段异步流程加总的截止时间。
   *
   * 为什么必须有：__qbBusy 期间关闭键、遮罩、Escape 全被拦下（那是防止旧抽屉被提前
   * 露出来的挡板），所以这段时间里任何一处无期限等待都会把用户永久困在弹窗里。
   * 而这条链上到处是没有超时的等待：utils.js 的 addMultipleToCart 里
   * /cart/add.js 和 cartSyncNow() 都是无期限的，刷新那边 res.text() 也是。
   *
   * 用 Promise.race 而不是只靠 AbortController：本店的 window.fetch 被
   * cart-drawer.js 包过一层，第三方 app 还可能再包，任何一层不透传 signal，
   * 基于 abort 的超时就失效了。race 的计时器不依赖任何一层实现。
   */
  var QB_TIMEOUT = {};

  function withDeadline(promise, ms, onTimeout) {
    var timer = null;
    return Promise.race([
      promise,
      new Promise(function (resolve) {
        timer = setTimeout(function () {
          if (typeof onTimeout === 'function') {
            try {
              onTimeout();
            } catch (e) {
              /* abort 失败无所谓，race 已经赢了 */
            }
          }
          resolve(QB_TIMEOUT);
        }, ms);
      })
    ]).then(
      function (value) {
        if (timer) clearTimeout(timer);
        return value;
      },
      /*
       * ⚠ 失败路径也必须清计时器。只在 fulfillment 里 clear 的话，
       * 请求提前 reject（网络错误 / HTTP 非 2xx / abort）之后计时器还活着，
       * 8~15 秒后照样跑一遍 onTimeout —— 对一个早就失败的请求补一次 abort()，
       * 而调用方那时候多半已经走完降级逻辑了。
       * 不用 .finally 是因为两参数 then 的兼容面更宽，且这里只有两条路径。
       */
      function (err) {
        if (timer) clearTimeout(timer);
        throw err;
      }
    );
  }

  /* ---------- 加购 ---------- */

  async function addToCart(modal) {
    var addBtn = modal.querySelector('[data-qb-add]');
    var addLabel = modal.querySelector('[data-qb-add-label]');
    var variantId = parseInt(addBtn.getAttribute('data-variant-id'), 10);
    if (!variantId || addBtn.disabled || modal.__qbBusy) return;
    var quantity = readQuantity(modal);

    modal.__qbBusy = true;
    addBtn.disabled = true;
    setQuantityLocked(modal, true);
    setError(modal, '');
    var restore = addLabel ? addLabel.textContent : '';
    if (addLabel) addLabel.textContent = copy(modal, 'adding', 'ADDING…');
    setStatus(modal, copy(modal, 'adding_status', 'Adding to cart'));

    try {
      if (typeof addMultipleToCart !== 'function') {
        throw new Error('addMultipleToCart 不存在（utils.js 没加载？）');
      }
      /*
       * addMultipleToCart 内部是「POST /cart/add.js → cartSyncNow()」，两段都没有超时。
       * 15 秒还没回来就不再等：这时候车里到底加没加进去是不确定的，
       * 唯一可靠的做法是整页刷新，让页面按服务端的真实购物车重新渲染。
       */
      var results = await withDeadline(addMultipleToCart([{ id: variantId, quantity: quantity }]), 15000);
      if (results === QB_TIMEOUT) {
        console.error('[quick-buy] 加购超时，整页刷新核对真实购物车');
        window.location.reload();
        return;
      }
      var failed = (results || []).filter(function (r) {
        return r.status !== 'success';
      });
      if (failed.length) {
        var d = failed[0].data || {};
        throw new Error(d.description || d.message || copy(modal, 'add_failed', 'Something went wrong. Please try again.'));
      }

      track('quick_buy_add', {
        popup_name: 'quick_buy',
        fired_from: modal.__qbSource || modal.getAttribute('data-source') || 'unknown'
      });

      if (addLabel) addLabel.textContent = copy(modal, 'added', 'ADDED ✓');
      addBtn.classList.add('is-added');
      setStatus(modal, copy(modal, 'added_status', 'Added to cart'));

      /*
       * 刷新和「ADDED ✓」的 600ms 并行跑，但要等**两者都完成**才关弹窗。
       *
       * 只等 600ms 就关是不行的：从购物车推荐位打开时，底下的抽屉一直是 active。
       * 刷新超过 600ms 的话，弹窗一关，用户立刻看到并且能操作一个内容还是旧的抽屉；
       * 等旧请求回来，refreshCartDrawer 的 innerHTML 又会把用户刚改的数量、
       * 刚点的删除整片覆盖掉。弹窗和它的遮罩在这里正好当挡板用，别提前撤掉。
       */
      var pendingRefresh = Promise.resolve(true); // addMultipleToCart has already refreshed the cart.
      var settled = await Promise.all([
        pendingRefresh,
        new Promise(function (resolve) {
          setTimeout(resolve, 600);
        })
      ]);

      // 刷新失败：更不能关 —— 关了就是把一个"加购成功但车里没这件"的旧抽屉露出来
      if (settled[0] === false) {
        window.location.reload();
        return;
      }

      addBtn.classList.remove('is-added');
      if (addLabel) addLabel.textContent = restore;
      modal.__qbBusy = false;
      addBtn.disabled = false;
      setQuantityLocked(modal, false);

      /*
       * close() 会把 lastOpener 清空，先取下来交给抽屉当"返回焦点"。
       * cart-drawer.js:35 只在收到 triggeredBy 时才 setActiveElement，
       * 而它 close() 时靠的正是这个值（cart-drawer.js:64）。不传的话，
       * 键盘用户关掉这个抽屉，焦点要么留在已经隐藏的抽屉里，要么被送回
       * 上一次开抽屉的那个旧元素（比如 header 的购物车图标）。
       * 此刻刷新已经落地：抽屉推荐位那条路径的按钮已被换掉，document.contains
       * 会把它挡掉（那条路径抽屉本来就开着，也不会再调 open）；
       * PDP 猜你喜欢的按钮不受刷新影响，会留下。
       */
      var backTo = lastOpener && document.contains(lastOpener) ? lastOpener : null;
      close(modal);
      await openCartDrawer(pendingRefresh, backTo);
    } catch (err) {
      console.error('[quick-buy] 加购失败', err);
      setError(modal, err.message || copy(modal, 'add_failed', 'Something went wrong. Please try again.'));
      // 错误详情由 role="alert" 播报；这里清掉旧的进度，保证下次重试写入
      // 同一句 "Adding to cart" 时仍会被 live region 当成一次新变化播报。
      setStatus(modal, '');
      if (addLabel) addLabel.textContent = restore;
      modal.__qbBusy = false;
      addBtn.disabled = false;
      setQuantityLocked(modal, false);
    }
  }

  /*
   * 刷新抽屉内容。
   *
   * 为什么不直接用 cart-drawer-items 的 onCartDrawerUpdate()：
   * 它只替换 <cart-drawer-items> 自己的 innerHTML（cart.js:114），而抽屉里的
   * 「你可能喜欢」推荐位在那个元素**外面** —— cart-drawer.liquid 里
   * <cart-drawer-items> 是 270-777 行，推荐位从 994 行才开始。
   * 只刷前者的话，刚加进车的那件商品仍然留在推荐列表里（cart_ids 排除逻辑没重跑），
   * 顾客可以对着同一张卡再点一次，重复加购。
   *
   * 所以这里自己取一次 section，两块一起换 —— 一次请求覆盖两个区域，
   * 比「onCartDrawerUpdate + 再来一次 fetch」少一个往返。
   *
   * 返回 false = 没刷上。调用方必须当失败处理，绝不能带着旧内容开抽屉。
   */
  async function refreshCartDrawer() {
    try { await CartFlow.refresh(); return true; }
    catch (error) { CartFlow.error(error.message); return false; }
  }

  /*
   * 打开抽屉。必须**等刷新完成**再开 —— 照 price.js:333-345 和
   * products-grid-swatch.js:150-157 的既有做法：
   *
   * 1) 不等的话网络慢时顾客先看到旧购物车，过一会儿才跳成新的；刷新失败时更糟，
   *    看到的是「加购成功但车里没这件」，会以为没加上再点一次，造成重复加购。
   * 2) 空车首次加购：is-empty 挂在**外层** <cart-drawer>（cart-drawer.liquid:172），
   *    而 cart-drawer.js:103 只从 .drawer__inner 上摘。留着它，open() 会按
   *    cart-drawer.js:47-53 把焦点圈定到已经隐藏的 .drawer__inner-empty 上。
   */
  async function openCartDrawer(pendingRefresh, triggeredBy) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    // 调用方可以把刷新提前发出去（和成功态动画并行），这里只负责等它落地
    if ((await (pendingRefresh || refreshCartDrawer())) === false) {
      window.location.reload();
      return;
    }

    var wasActive = drawer.classList.contains('active');
    drawer.classList.remove('is-empty');

    /*
     * ⚠ 抽屉已经开着就**不要**再调 open()。
     * cart-drawer.js:34 的 open() 每次都注册一个 {once:true} 的 transitionend，
     * 而 active 抽屉不会发生打开过渡 —— 那条监听会一直挂着，
     * 等用户后来关抽屉时被**关闭动画**的 transitionend 触发，
     * 把焦点重新圈进一个正在关闭/已经隐藏的抽屉里。
     * 这种情况下抽屉本来就开着、焦点也已经由 close() 还给了推荐按钮，
     * 什么都不用做 —— 它自己的 activeElement 还是当初开它的那个元素，本来就对。
     */
    if (window.CartFlow?.displayStale) {
      CartFlow.recoverDisplay(true);
      return;
    }
    if (!wasActive) {
      try {
        if (typeof drawer.open === 'function') {
          // 传 opener：抽屉据此登记 activeElement，关闭时焦点才能回到那颗 ADD
          drawer.open(triggeredBy && document.contains(triggeredBy) ? triggeredBy : undefined);
        }
      } catch (e) {
        console.error('[quick-buy] 打开购物车抽屉失败', e);
      }
    }


  }

  /* ---------- 事件委托 ---------- */

  /*
   * 按需加载弹窗（购物车抽屉推荐位用）。
   *
   * PDP 的 also-like 最多 4 张卡，弹窗直接由 Liquid 预渲染进页面，点开零延迟。
   * 抽屉不行 —— 它在全站每页渲染，推荐条数跟着购物车走没有上限，
   * 预渲染会让所有页面的 HTML 线性膨胀。所以那边只挂一个 ?view=quickbuy 地址，
   * 点击时才把弹窗取回来（模板：templates/product.quickbuy.liquid）。
   *
   * 取失败时退回打开产品页，绝不能让按钮点了没反应。
   */
  var loadingModals = {};

  /*
   * 「最后一次点击说了算」的令牌。
   * 按需加载是异步的，只按产品 id 去重挡不住这两种情况：
   *   1. 先点 A 再点 B，A 的请求后返回 —— 会把已经开着的 B 关掉、改开 A；
   *   2. 同一张卡快速点两下 —— 两次 await 落在同一个 Promise 上，
   *      但各自都会调一次 open()，quick_buy_open 因此重复上报。
   * 每次点击领一个递增号，await 回来后不是最新号就直接放弃。
   */
  var openSeq = 0;

  /*
   * 只负责「把弹窗取回来」，不做任何导航。
   * 返回 { modal, failed } —— 失败后要不要跳产品页由调用方决定。
   *
   * ⚠ 降级跳转绝不能放在这里面：这个 Promise 会被多次点击共享，而且完成得可能很晚。
   * 「先点 A，再点 B（B 已打开），A 最后失败」的情况下，在这里跳转会把用户从
   * 正开着的 B 强行带走 —— 成功路径遵守了「最后一次点击说了算」，失败路径也必须遵守。
   */
  /*
   * 弹窗加载失败时的就地提示（只给带 data-quick-buy-inline-fallback 的按钮用）。
   * 样式行内写死、和购物车编辑器载入失败的提示同一套（component-cart-edit-loader.js 的 fallback）：
   * 失败的可能正是样式表本身。用 span + display:block，因为按钮常在 <p> / <li> 里。
   */
  function showInlineFallback(opener, href) {
    var host = opener.parentElement;
    if (!host) return;
    var old = host.querySelector('.cart-add-fallback');
    if (old) old.remove();
    var note = document.createElement('span');
    note.className = 'cart-add-fallback';
    note.setAttribute('role', 'alert');
    note.style.cssText = 'display:block;margin:4px 0 0;font-size:12px;line-height:1.4;color:#cc2626;text-transform:none;';
    note.appendChild(document.createTextNode('Couldn’t load the options. Please try again'));
    if (href) {
      note.appendChild(document.createTextNode(' or '));
      var link = document.createElement('a');
      link.href = href;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = 'open the product page';
      link.style.cssText = 'color:inherit;text-decoration:underline;';
      note.appendChild(link);
    }
    note.appendChild(document.createTextNode('.'));
    opener.after(note);
  }

  async function loadModal(opener, id) {
    var url = opener.getAttribute('data-quick-buy-url');
    if (!url) return { modal: null, failed: false };
    if (loadingModals[id]) return loadingModals[id];

    opener.disabled = true;
    opener.setAttribute('aria-busy', 'true');
    loadingModals[id] = (async function () {
      /*
       * 必须有截止时间。这里先把按钮 disable 了，而 finally 只在 Promise settle
       * 之后才跑 —— 连接一挂，按钮就永久停在 disabled，也不会走降级跳转，
       * 顾客面对的是一颗永远点不动的 ADD。
       * 和刷新那边一样，deadline 要罩住「发请求 + 读完 body」整体：
       * 响应头回来 fetch 就 resolve 了，res.text() 仍可无限等。
       */
      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      try {
        var html = await withDeadline(
          (async function () {
            var res = await fetch(url, ctrl ? { signal: ctrl.signal } : undefined);
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return await res.text();
          })(),
          8000,
          function () {
            if (ctrl) ctrl.abort();
          }
        );
        if (html === QB_TIMEOUT) throw new Error('弹窗加载超时');

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('[data-quick-buy-modal]');
        if (!node) throw new Error('返回的 HTML 里没有弹窗节点');
        var imported = document.importNode(node, true);
        if (imported.id !== id) throw new Error('返回的弹窗 id 与请求不一致');

        /*
         * 这里只返回**未挂载**节点，不提前写进 body。
         * await 期间 opener 可能已经被抽屉刷新换掉，或者抽屉已经关过；如果先 append
         * 再由调用方判作废，这个无人使用的旧弹窗会逃过此前的缓存清理并留在 body，
         * 下次同 id 推荐出现时还会被当成有效缓存直接打开。
         */
        return { modal: imported, failed: false };
      } catch (e) {
        console.error('[quick-buy] 弹窗加载失败', e);
        return { modal: null, failed: true };
      } finally {
        opener.disabled = false;
        opener.removeAttribute('aria-busy');
        delete loadingModals[id];
      }
    })();
    return loadingModals[id];
  }

  document.addEventListener('click', async function (event) {
    var opener = event.target.closest('[data-quick-buy-open]');
    if (opener) {
      event.preventDefault();
      var myToken = ++openSeq;
      var id = opener.getAttribute('data-quick-buy-open');
      var staleNote = opener.parentElement && opener.parentElement.querySelector('.cart-add-fallback');
      if (staleNote) staleNote.remove();
      // 来源要在点击的**同步**阶段就记下来：await 之后 opener 可能已经被
      // refreshCartDrawer 换掉，那时再问它在不在抽屉里已经问不出来了
      var sourceDrawer = opener.closest('cart-drawer');
      var fromDrawer = !!sourceDrawer;

      var modal = document.getElementById(id);
      var failed = false;
      var closeWatch = fromDrawer && !modal ? watchDrawerClosure(sourceDrawer) : null;
      if (!modal) {
        var res = await loadModal(opener, id);
        modal = res.modal;
        failed = res.failed;
      }

      // disconnect 前先同步消费尚未送达回调的 mutation records，避免边界时序漏判。
      var drawerWasClosed = closeWatch ? closeWatch.wasClosed() : false;
      if (closeWatch) closeWatch.disconnect();

      /*
       * await 期间世界可能已经变了，三件事都要重新确认：
       *   1. 这次点击还是不是最新的（否则先点的会顶掉后点的）；
       *   2. 触发按钮还在不在文档里（加购刷新会把整片推荐位换掉）；
       *   3. 如果是从购物车抽屉点的，抽屉是不是还开着 ——
       *      用户完全可能在请求返回前就把抽屉关了，这时候再弹出来
       *      就成了"抽屉关了弹窗自己冒出来"。
       * 任何一条不成立，这次点击就作废，连降级跳转也不做。
       */
      var stillWanted =
        myToken === openSeq &&
        document.contains(opener) &&
        !drawerWasClosed &&
        (!fromDrawer || isDrawerActive());
      if (!stillWanted) return;

      if (modal) {
        /*
         * loadModal 返回的是 detached 节点；只有点击仍有效才提交 DOM 副作用。
         * 极少数情况下等待期间别的路径已放入同 id 节点，优先复用现有节点，
         * 避免制造重复 id。
         */
        if (!document.contains(modal)) {
          var existing = document.getElementById(id);
          if (existing) {
            modal = existing;
          } else {
            document.body.appendChild(modal);
          }
        }
        /*
         * 埋点来源按**这次点的按钮**算，每次打开都重设。
         * ?view=quickbuy 模板把 data-source 写死成 cart-drawer-recommendation（Liquid 读不到 URL 参数），
         * PDP 挂法浮窗的配件链接也拉同一个模板、同一个 qb-cartrec-<id>，弹窗节点是共用缓存的 ——
         * 写回 data-source 的话谁先加载就一直记成谁。按钮带 data-quick-buy-source 的用它，
         * 没带的（抽屉推荐、also-like）清空后回落到弹窗自己的 data-source。
         * 不改 data-source 属性本身：cart:rendered 的过期清理靠它找抽屉推荐的弹窗。
         */
        modal.__qbSource = opener.getAttribute('data-quick-buy-source') || '';
        open(modal, opener);
      } else if (failed) {
        /*
         * 降级用 location.assign 不用 window.open：这里已在 await 之后，
         * 用户激活多半过期，慢网络下 window.open 会被当弹窗拦掉，
         * 结果按钮点了彻底没反应，比跳走更糟。
         */
        var href = opener.getAttribute('data-product-url');
        if (opener.hasAttribute('data-quick-buy-inline-fallback')) {
          // 开在配置流程中间的入口（PDP 挂法浮窗的配件链接）：跳走会丢掉没加购的窗帘配置。
          // 留在当前页提示重试，另给一个用户自己点的产品链接（新标签页）。
          showInlineFallback(opener, href);
        } else if (href) {
          window.location.assign(href);
        }
      }
      return;
    }

    if (event.target.closest('[data-qb-close]')) {
      var m = event.target.closest('[data-quick-buy-modal]');
      if (m) {
        event.preventDefault();
        /*
         * 加购+刷新还没走完时不许关。
         * 弹窗和它的遮罩在这段时间是挡板：提前关掉的话旧抽屉立刻可操作，
         * 用户改完数量/删了行，稍后那次刷新回来又用旧响应的 innerHTML 盖掉。
         * 这正是上面「等刷新完成再撤挡板」要防的事，关闭路径必须一起守。
         */
        if (!m.__qbBusy) close(m);
      }
      return;
    }

    var swatch = event.target.closest('.qb-swatch');
    if (swatch) {
      var modalEl = swatch.closest('[data-quick-buy-modal]');
      var group = swatch.closest('[data-qb-option]');
      if (modalEl && group) {
        event.preventDefault();
        /*
         * 加购进行中不许改选项。
         * 提交时变体号已经固定，这里再让人改，图片/价格/选中态会跟着变，
         * 最后「ADDED ✓」显示在红色/M 上，实际进车的却是之前的蓝色/S。
         */
        if (modalEl.__qbBusy) return;
        /*
         * 已售罄的取值不接受选择 —— 标了 aria-disabled 又能点是自相矛盾的，
         * 读屏用户点下去只会更困惑。这里给一句播报说明为什么没反应。
         */
        var clicked = swatch.getAttribute('data-qb-value') || '';

        // 真·售罄（任何组合都没货）：不接受选择，说明原因
        if (swatch.classList.contains('is-unavailable')) {
          var strings = window.variantStrings || {};
          setStatus(modalEl, clicked + ' — ' + (strings.soldOut || 'Sold out'));
          return;
        }

        /*
         * "别处有货、但和当前其它选项搭不上"：不能挡，挡了就会形成走不到的库存孤岛。
         * 直接跳到含这个取值的第一个可售变体，把其它选项一起调过去，
         * 并播报一声——否则读屏用户只会发现尺寸莫名其妙自己变了。
         */
        if (swatch.classList.contains('is-unmatched')) {
          var gIdx = parseInt(group.getAttribute('data-option-index'), 10);
          var all = readVariants(modalEl);
          var target = null;
          for (var t = 0; t < all.length; t++) {
            if (all[t].opts[gIdx] === clicked && all[t].available) {
              target = all[t];
              break;
            }
          }
          if (target) {
            modalEl.querySelectorAll('[data-qb-option]').forEach(function (g) {
              var i = parseInt(g.getAttribute('data-option-index'), 10);
              selectValue(g, target.opts[i]);
            });
            render(modalEl);
            setStatus(modalEl, copy(modalEl, 'selection_updated', 'Selection updated to __options__').replace('__options__', function () { return target.opts.join(' / '); }));
            return;
          }
        }

        selectValue(group, clicked);
        render(modalEl);
      }
      return;
    }

    var add = event.target.closest('[data-qb-add]');
    if (add) {
      var host = add.closest('[data-quick-buy-modal]');
      if (host) {
        event.preventDefault();
        addToCart(host);
      }
    }
  });

  // 数量变了（步进器发 change，手输发 input）→ 更新按钮上的「× N」
  ['input', 'change'].forEach(function (type) {
    document.addEventListener(type, function (event) {
      var input = event.target.closest && event.target.closest('[data-qb-quantity]');
      var modal = input && input.closest('[data-quick-buy-modal]');
      if (modal) renderQuantity(modal);
    });
  });

  document.addEventListener('keyup', function (event) {
    // 同上：加购未落地时 Escape 也不放行
    if (event.key === 'Escape' && activeModal && !activeModal.__qbBusy) close(activeModal);
  });
  document.addEventListener('cart:rendered', function () {
    document.querySelectorAll('body > [data-quick-buy-modal][data-source="cart-drawer-recommendation"]').forEach(function (modal) {
      if (modal === activeModal) modal.__qbStale = true;
      else modal.remove();
    });
  });
})();
